import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';

import styles from './invoice-manager/invoiceManagerStyles';
import InvoiceDetailCard from './invoice-manager/InvoiceDetailCard';
import LineItemsEditor from './invoice-manager/LineItemsEditor';
import DiscountCodeSection from './invoice-manager/DiscountCodeSection';
import InvoiceDiscountSection from './invoice-manager/InvoiceDiscountSection';
import InvoicePreviewPanel from './invoice-manager/InvoicePreviewPanel';
import InvoiceBillsTable from './invoice-manager/InvoiceBillsTable';
import InvoiceDangerZone from './invoice-manager/InvoiceDangerZone';
import AddressModal from './invoice-manager/AddressModal';

/**
 * Invoice Manager - Admin tool for managing sent invoices
 *
 * Features:
 * - Load invoice by number
 * - Edit line items (quantity, rate, discount)
 * - Add/remove line items
 * - Apply invoice-level discount (percentage or fixed)
 * - Preview changes with impact analysis
 * - Apply changes with optional email to customer
 * - Edit billing address
 * - Void/delete invoices
 */
interface InvoiceManagerProps {
  initialInvoiceNumber?: string;
}

interface InvoiceData {
  invoice_id: string;
  invoice_number: string;
  customer_name: string;
  customer_email: string;
  total: number;
  status: string;
  billing_address?: { address?: string } | null;
  [key: string]: unknown;
}

interface LineItemData {
  line_item_id?: string;
  item_id?: string;
  name: string;
  quantity: number;
  rate: number;
  discount: number;
  tax_id?: string;
  section_label?: string | null;
  section_quantity?: number;
  [key: string]: unknown;
}

interface BillData {
  id: string;
  [key: string]: unknown;
}

interface LocalInvoiceData {
  invoice_id?: string;
  service_log_id?: string;
  [key: string]: unknown;
}

interface CodeValidationResult {
  valid: boolean;
  message: string;
  code_type?: 'referral' | 'voucher';
  discount_amount?: number;
  voucher_id?: string;
  referral_code_id?: string;
  payout_type?: 'discount' | 'commission';
  // True when a commission code is also a live collaboration, which discounts
  // the customer as well as paying the partner. Plain agent codes are false.
  collab_discount?: boolean;
  [key: string]: unknown;
}

function InvoiceManager({ initialInvoiceNumber }: InvoiceManagerProps) {
  const { supabase, hasPermission } = useAuth();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('invoices');
  const canEdit = hasPermission('invoices', 'manage');
  const canUnlinkDraft = hasPermission('invoices', 'manage');
  const configContext = useConfig() || {};
  const currencyCode = configContext.currencyCode || '';
  const locale = configContext.config?.locale || undefined;

  const formatCurrency = configContext.formatCurrency || ((amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(amount || 0);
  });

  // State
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [originalLineItems, setOriginalLineItems] = useState<LineItemData[]>([]);
  const [lineItems, setLineItems] = useState<LineItemData[]>([]);
  const [bills, setBills] = useState<BillData[]>([]);
  const [localData, setLocalData] = useState<LocalInvoiceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Discount state
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');

  // Discount code state (voucher / referral)
  const [discountCode, setDiscountCode] = useState('');
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeValidation, setCodeValidation] = useState<CodeValidationResult | null>(null);
  // A voucher/referral discount already on the loaded invoice (set at booking
  // carry-through or by an earlier apply). Blocks a second code being applied
  // on top, which would otherwise trip the invoice integrity check.
  const [appliedCodeDiscount, setAppliedCodeDiscount] = useState(0);
  // The voucher or collaboration part only, held on the invoice in
  // referral_discount_applied. It survives an edit untouched and the invoice total check
  // subtracts it, so the figures on this screen must subtract it too or the total shown
  // is the one the database refuses (INV-ZA-025147, 2026-09-15).
  const [codeDiscount, setCodeDiscount] = useState(0);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);

  // Address modal state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newBillingAddress, setNewBillingAddress] = useState('');
  const [addressChangeReason, setAddressChangeReason] = useState('');

  // API helper
  const apiCall = useCallback(async (action: string, params: Record<string, unknown> = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-edit`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, ...params }),
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `Request failed: ${response.status}`);
    }
    return data;
  }, [supabase]);

  // Calculate line total
  const calculateLineTotal = (item: { quantity?: number | string; rate?: number | string; discount?: number | string; [key: string]: unknown }) => {
    const subtotal = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
    const discount = Number(item.discount) || 0;
    return subtotal - discount;
  };

  // Calculate totals — per-room line totals are multiplied by section_quantity
  // to match the backend convention in invoice-edit/index.ts.
  const calculateTotals = useCallback(() => {
    const subtotal = lineItems.reduce((sum, item) => {
      const sq = Math.max(1, Number(item.section_quantity) || 1);
      return sum + (calculateLineTotal(item) * sq);
    }, 0);

    let invoiceDiscount = 0;
    const dv = parseFloat(discountValue) || 0;
    if (dv > 0) {
      if (discountType === 'percentage') {
        invoiceDiscount = subtotal * (dv / 100);
      } else if (discountType === 'amount') {
        invoiceDiscount = dv;
      }
    }

    const finalTotal = Math.max(0, subtotal - invoiceDiscount - codeDiscount);
    return { subtotal, invoiceDiscount, finalTotal };
  }, [lineItems, discountType, discountValue, codeDiscount]);

  // Status badge color
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'var(--color-text-light)',
      sent: 'var(--color-info)',
      viewed: '#8b5cf6',
      paid: 'var(--color-success)',
      partial: 'var(--color-warning)',
      overdue: 'var(--color-error)',
      void: 'var(--color-error)',
    };
    return colors[status] || 'var(--color-text-light)';
  };

  // Load invoice
  const loadInvoice = async () => {
    if (!invoiceNumber.trim()) {
      setError('Please enter an invoice number');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    setInvoice(null);
    setShowPreview(false);
    setDiscountType('none');
    setDiscountValue('');
    setAdjustmentReason('');
    setDiscountCode('');
    setCodeValidation(null);

    try {
      const data = await apiCall('get', { invoice_number: invoiceNumber.trim() });

      setInvoice(data.invoice);
      const items = (data.line_items || []).map((item: Record<string, unknown>) => ({
        line_item_id: item.line_item_id,
        item_id: item.item_id,
        name: item.name,
        quantity: item.quantity,
        rate: item.rate,
        discount: item.discount || 0,
        tax_id: item.tax_id,
        section_label: (item.section_label as string | null) ?? null,
        section_quantity: Math.max(1, Number(item.section_quantity) || 1),
      }));
      setLineItems(items);
      setOriginalLineItems(JSON.parse(JSON.stringify(items)));
      setBills(data.bills || []);
      setLocalData(data.local_data);

      // Restore existing invoice-level discount so admin can see it
      const inv = data.invoice;
      const existingDiscount = Number(inv?.discount_total || 0);
      if (existingDiscount > 0) {
        setDiscountType('amount');
        setDiscountValue(String(existingDiscount));
      }
      setAppliedCodeDiscount(existingDiscount + Number(inv?.referral_discount_applied || 0));
      setCodeDiscount(Number(inv?.referral_discount_applied || 0));

      setMessage('Invoice loaded successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Update line item
  const updateLineItem = (index: number, field: string, value: string | number) => {
    const updated = [...lineItems];
    const item = updated[index];
    if (!item) return;
    if (['quantity', 'rate', 'discount'].includes(field)) {
      item[field] = parseFloat(String(value)) || 0;
    } else {
      item[field] = value;
    }
    setLineItems(updated);
  };

  // Add new line. When a section context is given, the new row inherits the
  // section label and multiplier so the per-section Add button drops it into
  // the right group. The new row is inserted directly after the last row of
  // the matching section to keep the table reading order tidy.
  const addNewLine = (section?: { label: string | null; quantity: number }) => {
    const newRow: LineItemData = {
      name: 'New Item',
      quantity: 1,
      rate: 0,
      discount: 0,
      section_label: section?.label ?? null,
      section_quantity: Math.max(1, section?.quantity ?? 1),
    };
    if (!section) {
      setLineItems([...lineItems, newRow]);
      return;
    }
    const targetLabel = section.label ?? null;
    let insertAt = lineItems.length;
    for (let i = lineItems.length - 1; i >= 0; i--) {
      if ((lineItems[i]?.section_label ?? null) === targetLabel) {
        insertAt = i + 1;
        break;
      }
    }
    const next = [...lineItems];
    next.splice(insertAt, 0, newRow);
    setLineItems(next);
  };

  // Rename a section. Writes the new label to every row in the group.
  const renameSection = (currentLabel: string | null, nextLabel: string | null) => {
    setLineItems(lineItems.map((item) =>
      (item.section_label ?? null) === currentLabel
        ? { ...item, section_label: nextLabel }
        : item
    ));
  };

  // Update a section's multiplier. Writes to every row in the group.
  const updateSectionQuantity = (label: string | null, quantity: number) => {
    const safe = Math.max(1, Math.floor(quantity) || 1);
    setLineItems(lineItems.map((item) =>
      (item.section_label ?? null) === label
        ? { ...item, section_quantity: safe }
        : item
    ));
  };

  // Add a brand new section. Drops a starter row in immediately so the user
  // sees the group and can edit the first line straight away.
  const addNewSection = (label: string, quantity: number) => {
    const cleanLabel = label.trim();
    if (!cleanLabel) return;
    const safeQty = Math.max(1, Math.floor(quantity) || 1);
    setLineItems([
      ...lineItems,
      {
        name: 'New Item',
        quantity: 1,
        rate: 0,
        discount: 0,
        section_label: cleanLabel,
        section_quantity: safeQty,
      },
    ]);
  };

  // Remove line
  const removeLine = (index: number) => {
    if (lineItems.length <= 1) {
      setError('Cannot remove last line item');
      return;
    }
    const updated = [...lineItems];
    updated.splice(index, 1);
    setLineItems(updated);
  };

  // Reset line items
  const resetLineItems = () => {
    setLineItems(JSON.parse(JSON.stringify(originalLineItems)));
    setDiscountType('none');
    setDiscountValue('');
    setDiscountCode('');
    setCodeValidation(null);
    setShowPreview(false);
    setMessage('Line items reset to original');
  };

  // Validate discount code (voucher or referral)
  const handleValidateCode = async () => {
    if (!discountCode.trim()) return;
    setCodeValidating(true);
    setCodeValidation(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { subtotal } = calculateTotals();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/code-validate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: discountCode.trim(),
            referee_email: invoice?.customer_email || '',
            mode: 'service_log',
            service_amount: subtotal,
            // Pass the service log so the voucher single-use check ignores this
            // invoice's own booking redemption (lets staff apply the discount
            // the customer was promised at booking).
            service_log_id: localData?.service_log_id || null,
          }),
        }
      );

      const result = await response.json();
      setCodeValidation(result);

      // A collaboration code is commission-type but still discounts the customer.
      // Only a plain agent commission code leaves the price alone.
      const commissionOnly = result.payout_type === 'commission' && !result.collab_discount;

      if (result.valid && result.discount_amount > 0 && !commissionOnly) {
        setDiscountType('amount');
        setDiscountValue(String(result.discount_amount));
        setAdjustmentReason(prev =>
          prev || `Discount code ${discountCode.trim().toUpperCase()} applied`
        );
      } else if (result.valid && commissionOnly) {
        setAdjustmentReason(prev =>
          prev || `Partner commission code ${discountCode.trim().toUpperCase()} applied`
        );
      }
    } catch (err) {
      setCodeValidation({ valid: false, message: err instanceof Error ? err.message : 'Validation failed' });
    } finally {
      setCodeValidating(false);
    }
  };

  // Clear validated code
  const handleClearCode = () => {
    setDiscountCode('');
    setCodeValidation(null);
    setDiscountType('none');
    setDiscountValue('');
  };

  // Redeem code + update local records after invoice update succeeds
  const redeemAndPersistCode = async () => {
    if (!codeValidation?.valid) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const code = discountCode.trim().toUpperCase();
      const codeType = codeValidation.code_type;
      const serviceLogId = localData?.service_log_id;
      const localInvoiceId = localData?.invoice_id;

      // 1. Call code-redeem (Sentinel uses separate admin-auth function)
      const redeemResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/code-redeem`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            referee_email: invoice?.customer_email || '',
            service_log_id: serviceLogId || null,
            mode: 'service_log',
            code_type: codeType,
          }),
        }
      );
      const redeemResult = await redeemResponse.json();

      // 2. Record which code produced the discount so the invoice PDF can
      //    label the discount line, e.g. "Discount (V-JOR-SLEEP250)". The
      //    discount amount itself is already on the invoice (discount_total)
      //    from the save above.
      if (localInvoiceId) {
        await supabase
          .from('invoices')
          .update({ discount_code: code, updated_at: new Date().toISOString() })
          .eq('id', localInvoiceId);
      }

      // 3. Update local service_log record with code fields
      if (serviceLogId) {
        const slUpdate: Record<string, unknown> = {};
        if (codeType === 'voucher') {
          slUpdate.voucher_id = codeValidation.voucher_id;
          slUpdate.voucher_discount_applied = codeValidation.discount_amount;
          slUpdate.code_type = 'voucher';
        } else if (codeType === 'referral') {
          slUpdate.referral_code_id = codeValidation.referral_code_id;
          slUpdate.referral_discount_applied = codeValidation.discount_amount;
          slUpdate.code_type = 'referral';
          if (redeemResult.redemption?.id) {
            slUpdate.referral_redemption_id = redeemResult.redemption.id;
          }
        }
        slUpdate.updated_at = new Date().toISOString();

        await supabase
          .from('service_logs')
          .update(slUpdate)
          .eq('id', serviceLogId);
      }

    } catch {
      // Failed to redeem/persist discount code
    }
  };

  // Apply changes
  const handleApplyChanges = async () => {
    if (!invoice) return;
    if (!adjustmentReason.trim()) {
      setError('Adjustment reason is required');
      return;
    }

    const { finalTotal } = calculateTotals();
    const confirmMsg = `Apply changes to ${invoice.invoice_number}?\n\nNew Total: ${formatCurrency(finalTotal)}\n\n${sendEmail ? 'Email WILL be sent to customer' : 'No email will be sent'}\n\nReason: ${adjustmentReason}`;

    if (!confirm(confirmMsg)) return;

    setLoading(true);
    setError(null);

    try {
      await apiCall('update', {
        invoice_id: invoice.invoice_id,
        invoice_number: invoice.invoice_number,
        line_items: lineItems,
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
        reason: adjustmentReason,
        send_email: sendEmail,
      });

      // If a discount code was validated, redeem it and persist to local DB
      if (codeValidation?.valid) {
        await redeemAndPersistCode();
      }

      // The invoice's payment_url is now a proxy URL of the form
      //   https://cleanmybed.com/pay/INVOICE_NUMBER
      // which fetches the live balance at click-time and creates a
      // Paystack link on the spot. So edits don't need to regenerate
      // anything — the same proxy URL keeps working at the new amount.
      // Previously this called generate_payment_link, which talked
      // directly to Paystack and stored a raw checkout.paystack.com
      // URL locked to the old amount. That's the inconsistency
      // operators were seeing in customer-facing links. Call removed
      // intentionally — do not re-add unless we move back off the
      // proxy pattern.

      setMessage(sendEmail
        ? 'Invoice updated and email sent to customer!'
        : 'Invoice updated successfully (no email sent)');
      setShowPreview(false);
      setCodeValidation(null);
      setDiscountCode('');
      setTimeout(loadInvoice, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Save address
  const handleSaveAddress = async () => {
    if (!invoice) return;
    if (!newBillingAddress.trim()) {
      setError('Please enter a billing address');
      return;
    }
    if (!addressChangeReason.trim()) {
      setError('Please provide a reason for the address change');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiCall('update_customer', {
        invoice_id: invoice.invoice_id,
        billing_address: newBillingAddress,
        reason: addressChangeReason,
      });

      setMessage('Billing address updated successfully');
      setShowAddressModal(false);
      setNewBillingAddress('');
      setAddressChangeReason('');
      setTimeout(loadInvoice, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Void invoice
  const handleVoid = async () => {
    if (!invoice) return;
    const reason = prompt('Enter reason for voiding this invoice:');
    if (!reason?.trim()) {
      setError('Reason is required');
      return;
    }

    if (!confirm(`VOID Invoice ${invoice.invoice_number}?\n\nThis will:\n- Mark the invoice as void in Zoho\n- Void all unpaid related bills\n\nReason: ${reason}`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiCall('void', {
        invoice_id: invoice.invoice_id,
        invoice_number: invoice.invoice_number,
        reason: reason.trim(),
      });

      setMessage(`Invoice voided successfully. ${data.bills_processed || 0} bills processed.`);
      setTimeout(loadInvoice, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Delete invoice
  const handleDelete = async () => {
    const invNumber = invoice?.invoice_number;

    if (!invNumber) {
      setError('No invoice number to delete');
      return;
    }

    const confirmCode = prompt(
      `DELETE ${invNumber}?\n\nThis CANNOT be undone!\n\nThis will permanently delete:\n- Invoice from Zoho Books (if exists)\n- All related bills from Zoho Books\n- Service log and all related records\n- All local database records\n\nType the invoice number to confirm:`
    );

    if (!confirmCode || confirmCode.trim() !== invNumber) {
      setError('Delete cancelled - confirmation code did not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let invoiceId = localData?.invoice_id;

      if (!invoiceId) {
        const { data: localInv } = await supabase
          .from('invoices')
          .select('id')
          .eq('invoice_number', invNumber)
          .maybeSingle();

        invoiceId = localInv?.id;
      }

      if (!invoiceId) {
        throw new Error('Cannot find local invoice record. The invoice may have already been deleted from the database.');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=delete&id=${invoiceId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Delete failed: ${response.status}`);
      }

      setMessage(`Invoice deleted permanently. ${data.deleted_items?.join(', ') || ''}`);
      setInvoice(null);
      setLineItems([]);
      setBills([]);
      setLocalData(null);
      setInvoiceNumber('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Unlink draft invoice
  const handleUnlinkDraft = async () => {
    if (!invoice || invoice.status !== 'draft') {
      setError('Only draft invoices can be unlinked');
      return;
    }

    const invNumber = invoice?.invoice_number;
    const confirmed = confirm(
      `⚠️ DELETE DRAFT INVOICE\n\n` +
      `Invoice: ${invNumber}\n` +
      `Customer: ${invoice.customer_name}\n` +
      `Amount: ${formatCurrency(Number(invoice.total))}\n\n` +
      `This will:\n` +
      `• Delete the draft invoice and all related bills\n` +
      `• Reverse any applied referral credits\n` +
      `• Return the service log to PENDING for editing\n` +
      `• Delete from Zoho Books if synced\n\n` +
      `The service log data (items, customer, etc.) will be preserved.\n\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let invoiceId = localData?.invoice_id;
      if (!invoiceId) {
        const { data: localInv } = await supabase
          .from('invoices')
          .select('id')
          .eq('invoice_number', invNumber)
          .maybeSingle();
        invoiceId = localInv?.id;
      }

      if (!invoiceId) {
        throw new Error('Cannot find local invoice record');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=unlink&id=${invoiceId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Unlink failed: ${response.status}`);
      }

      setMessage(`✅ ${data.message}\n\nDeleted: ${data.deleted?.join(', ') || 'Invoice deleted'}`);
      setInvoice(null);
      setLineItems([]);
      setBills([]);
      setLocalData(null);
      setInvoiceNumber('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const { finalTotal } = calculateTotals();
  const originalTotal = parseFloat(String(invoice?.total || 0));
  const totalAdjustment = originalTotal - finalTotal;

  // Auto-load invoice if initialInvoiceNumber is provided
  useEffect(() => {
    if (initialInvoiceNumber) {
      setInvoiceNumber(initialInvoiceNumber);
    }
  }, [initialInvoiceNumber]);

  // Load invoice when invoiceNumber is set from initialInvoiceNumber
  useEffect(() => {
    if (initialInvoiceNumber && invoiceNumber === initialInvoiceNumber && !invoice && !loading) {
      const autoLoadInvoice = async () => {
        setLoading(true);
        setError(null);
        setMessage(null);
        setInvoice(null);
        setShowPreview(false);
        setDiscountType('none');
        setDiscountValue('');
        setAdjustmentReason('');

        try {
          const data = await apiCall('get', { invoice_number: initialInvoiceNumber });

          setInvoice(data.invoice);
          const items = (data.line_items || []).map((item: Record<string, unknown>) => ({
            line_item_id: item.line_item_id,
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            rate: item.rate,
            discount: item.discount || 0,
            tax_id: item.tax_id,
            section_label: (item.section_label as string | null) ?? null,
            section_quantity: Math.max(1, Number(item.section_quantity) || 1),
          }));
          setLineItems(items);
          setOriginalLineItems(JSON.parse(JSON.stringify(items)));
          setBills(data.bills || []);
          setLocalData(data.local_data);

          // Restore existing invoice-level discount so admin can see it
          const inv = data.invoice;
          const existingDiscount = Number(inv?.discount_total || 0);
          if (existingDiscount > 0) {
            setDiscountType('amount');
            setDiscountValue(String(existingDiscount));
          }
          // Same two lines as the Load Invoice path above. Without them, arriving here
          // from a service log card left both at zero, so a voucher or collaboration
          // discount was invisible on this screen and the "a discount is already
          // applied" guard never fired (INV-ZA-025147, 2026-09-16).
          setAppliedCodeDiscount(existingDiscount + Number(inv?.referral_discount_applied || 0));
          setCodeDiscount(Number(inv?.referral_discount_applied || 0));

          setMessage('Invoice loaded successfully');
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
          setLoading(false);
        }
      };
      autoLoadInvoice();
    }
  }, [invoiceNumber, initialInvoiceNumber, invoice, loading, apiCall]);

  // Permission loading state
  if (permissionLoading) return null;

  // Access denied check
  if (!canAccess || !canEdit) {
    return <AccessDenied feature="invoices" />;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div className="mb-lg">
        <h1 style={styles.title}>Invoice Manager</h1>
        <p style={styles.subtitle}>Adjust invoices, manage discounts, and handle corrections</p>
      </div>

      {/* Invoice Loader */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Load Invoice</h2>
        <div className="flex gap-md">
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadInvoice()}
            placeholder="Enter invoice number (e.g., INV-000001)"
            style={styles.input}
          />
          <button onClick={loadInvoice} disabled={loading} style={styles.primaryBtn}>
            {loading ? 'Loading...' : 'Load Invoice'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && <div style={styles.errorBox}>{error}</div>}
      {message && <div style={styles.successBox}>{message}</div>}

      {/* Loading Spinner */}
      {loading && (
        <div className="fixed flex-center" style={{ inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50 }}>
          <div className="text-center" style={{ background: 'white', padding: '1.5rem', borderRadius: 'var(--radius-lg)' }}>
            <div style={styles.spinner} />
            <p>Processing...</p>
          </div>
        </div>
      )}

      {/* Invoice Details */}
      {invoice && (
        <>
          <InvoiceDetailCard
            invoice={invoice as unknown as Parameters<typeof InvoiceDetailCard>[0]['invoice']}
            localData={localData}
            formatCurrency={formatCurrency}
            getStatusColor={getStatusColor}
            onEditAddress={() => {
              setNewBillingAddress(invoice.billing_address?.address || '');
              setShowAddressModal(true);
            }}
          />

          {/* Notes */}
          <div style={{ background: 'var(--color-surface, white)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: '1.25rem', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 600, margin: '0 0 1rem' }}>Notes</h3>
            <div className="mb-md">
              <label style={{ display: 'block', fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '0.25rem' }}>Customer-facing (appears on invoice PDF)</label>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginBottom: '0.375rem' }}>Printed on the invoice PDF and visible to the customer.</div>
              <textarea
                className="form-textarea w-full"
                value={(invoice as Record<string, unknown>).notes as string || ''}
                onChange={(e) => setInvoice((prev) => prev ? { ...prev, notes: e.target.value } : prev)}
                onBlur={async (e) => {
                  if (!localData?.invoice_id) return;
                  await supabase.from('invoices').update({ notes: e.target.value || null }).eq('id', localData.invoice_id);
                }}
                rows={2}
                placeholder="e.g. Payment due within 7 days..."
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text-light)', marginBottom: '0.25rem' }}>Internal only (not visible to customer)</label>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginBottom: '0.375rem' }}>Only visible to staff. Not shown on the invoice or emailed to the customer.</div>
              <textarea
                className="form-textarea w-full"
                value={(invoice as Record<string, unknown>).internal_notes as string || ''}
                onChange={(e) => setInvoice((prev) => prev ? { ...prev, internal_notes: e.target.value } : prev)}
                onBlur={async (e) => {
                  if (!localData?.invoice_id) return;
                  await supabase.from('invoices').update({ internal_notes: e.target.value || null }).eq('id', localData.invoice_id);
                }}
                rows={2}
                placeholder="e.g. Billing contact is the accountant, not the property manager..."
              />
            </div>
          </div>

          <LineItemsEditor
            lineItems={lineItems}
            formatCurrency={formatCurrency}
            onUpdateLineItem={updateLineItem}
            onAddNewLine={addNewLine}
            onResetLineItems={resetLineItems}
            onRemoveLine={removeLine}
            calculateLineTotal={calculateLineTotal}
            onRenameSection={renameSection}
            onUpdateSectionQuantity={updateSectionQuantity}
            onAddNewSection={addNewSection}
          />

          <DiscountCodeSection
            discountCode={discountCode}
            onSetDiscountCode={setDiscountCode}
            codeValidating={codeValidating}
            codeValidation={codeValidation}
            onValidateCode={handleValidateCode}
            onClearCode={handleClearCode}
            customerEmail={invoice.customer_email}
            formatCurrency={formatCurrency}
            alreadyAppliedDiscount={appliedCodeDiscount}
            codeDiscount={codeDiscount}
          />

          <InvoiceDiscountSection
            discountType={discountType}
            onSetDiscountType={setDiscountType}
            discountValue={discountValue}
            onSetDiscountValue={setDiscountValue}
            currencyCode={currencyCode}
            onPreviewChanges={() => setShowPreview(true)}
          />

          {showPreview && (
            <InvoicePreviewPanel
              originalTotal={originalTotal}
              finalTotal={finalTotal}
              codeDiscount={codeDiscount}
              totalAdjustment={totalAdjustment}
              formatCurrency={formatCurrency}
              adjustmentReason={adjustmentReason}
              onSetAdjustmentReason={setAdjustmentReason}
              sendEmail={sendEmail}
              onSetSendEmail={setSendEmail}
              loading={loading}
              onApplyChanges={handleApplyChanges}
              onCancel={() => setShowPreview(false)}
            />
          )}

          <InvoiceBillsTable
            bills={bills}
            formatCurrency={formatCurrency}
            getStatusColor={getStatusColor}
          />

          <InvoiceDangerZone
            invoice={invoice}
            bills={bills}
            loading={loading}
            canUnlinkDraft={canUnlinkDraft}
            formatCurrency={formatCurrency}
            onVoid={handleVoid}
            onDelete={handleDelete}
            onUnlinkDraft={handleUnlinkDraft}
          />
        </>
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <AddressModal
          newBillingAddress={newBillingAddress}
          onSetNewBillingAddress={setNewBillingAddress}
          addressChangeReason={addressChangeReason}
          onSetAddressChangeReason={setAddressChangeReason}
          loading={loading}
          onSave={handleSaveAddress}
          onClose={() => setShowAddressModal(false)}
        />
      )}
    </div>
  );
}

export default InvoiceManager;
